import { getImetaInfoFromImetaTag } from '@/lib/tag'
import { cn, parseYouTubeURL } from '@/lib/utils'
import { getImetaTagByUrl } from '@/services/media-upload.service'
import { TImetaInfo } from '@/types'
import { Event } from '@nostr/tools/wasm'
import { parse } from '@nostr/tools/nip27'
import { neventEncode, naddrEncode, nprofileEncode } from '@nostr/tools/nip19'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { useNoteTranslation } from '@/atoms/translations'
import { EmbeddedHashtag, EmbeddedMention, EmbeddedNote, EmbeddedWebsocketUrl } from './Embedded'
import Emoji from './Emoji'
import ExternalLink from './ExternalLink'
import ImageGallery from './ImageGallery'
import MediaPlayer from './MediaPlayer'
import WebPreview from './WebPreview'
import YoutubeEmbeddedPlayer from './YoutubeEmbeddedPlayer'
import YoutubeStaticEmbed from './YoutubeStaticEmbed'
import { useTranslation } from 'react-i18next'
import { EmbedDepthContext, getMaxEmbedDepth, useEmbedDepth } from '@/lib/embedDepth'
import { toNote } from '@/lib/link'

export default function Content({
  event,
  content,
  className,
  mustLoadMedia,
  simpleEmbedId,
  clamp
}: {
  event?: Event
  content?: string
  className?: string
  mustLoadMedia?: boolean
  simpleEmbedId?: string
  clamp?: boolean
}) {
  const translation = useNoteTranslation(event)
  const isInflight = Boolean(translation && translation.inflight && !translation.showOriginal)
  const depth = useEmbedDepth()
  const maxEmbedDepth = getMaxEmbedDepth()

  const { t } = useTranslation()
  const displayEvent = useMemo(() => {
    if (!event) return undefined
    if (isInflight) return event
    if (!translation || translation.showOriginal) return event
    return { ...event, content: translation.content }
  }, [event, translation, isInflight])

  const nodes = useMemo(() => {
    const nodes: ReactElement[] = []
    const allImages: (string | TImetaInfo)[] = []
    let imageIndex = 0

    let prevIsNewline = true
    let prevIsCandidate: string | false = false

    for (const block of parse(displayEvent ?? content ?? '')) {
      switch (block.type) {
        case 'text': {
          prevIsNewline = false

          if (block.text.startsWith('\n\n') && prevIsCandidate) {
            setPreviouysNodeAsPreview(prevIsCandidate)
            prevIsCandidate = false
          }
          if (block.text.endsWith('\n\n')) {
            prevIsNewline = true
          } else {
            prevIsCandidate = false
          }

          // preserve newlines even in otherwise-empty text blocks
          if (block.text.trim() === '' && !block.text.includes('\n')) continue

          nodes.push(<span key={nodes.length}>{block.text}</span>)
          break
        }
        case 'url': {
          prevIsCandidate = false
          if (prevIsNewline) {
            prevIsCandidate = block.url
          }
          prevIsNewline = false

          nodes.push(<ExternalLink key={nodes.length} url={block.url} />)
          break
        }
        case 'relay': {
          prevIsNewline = false
          prevIsCandidate = false
          nodes.push(<EmbeddedWebsocketUrl key={nodes.length} url={block.url} />)
          break
        }
        case 'image': {
          prevIsNewline = false
          prevIsCandidate = false
          if (nodes.length > 0 && (nodes[nodes.length - 1].type as any).name === 'ImageGallery') {
            nodes[nodes.length - 1] = (
              <ImageGallery
                key={nodes[nodes.length - 1].key}
                className="mt-2"
                images={allImages}
                start={(nodes[nodes.length - 1].props as any).start}
                end={imageIndex + 1}
                mustLoad={mustLoadMedia}
              />
            )
          } else {
            nodes.push(
              <ImageGallery
                key={nodes.length}
                className="mt-2"
                images={allImages}
                start={imageIndex}
                end={imageIndex + 1}
                mustLoad={mustLoadMedia}
              />
            )
          }

          let imeta: TImetaInfo | null = null
          if (displayEvent) {
            let tag = displayEvent.tags.find(
              ([k, ...vals]) => k === 'imeta' && vals.find((val) => val === `url ${block.url}`)
            )
            if (!tag) tag = getImetaTagByUrl(block.url)
            if (tag) {
              imeta = getImetaInfoFromImetaTag(tag, displayEvent.pubkey)
            }
          }

          if (imeta) allImages.push(imeta)
          else allImages.push(block.url)

          imageIndex++
          break
        }
        case 'video':
        case 'audio': {
          prevIsNewline = false
          prevIsCandidate = false
          nodes.push(
            <MediaPlayer
              key={nodes.length}
              className="mt-2"
              src={block.url}
              pubkey={displayEvent?.pubkey}
              mustLoad={mustLoadMedia}
            />
          )
          break
        }
        case 'reference': {
          prevIsNewline = false
          prevIsCandidate = false
          if ('id' in block.pointer) {
            if (depth >= maxEmbedDepth) {
              nodes.push(
                <a
                  key={nodes.length}
                  href={toNote(block.pointer)}
                  className="mt-2 inline-block text-sm text-muted-foreground border rounded-lg px-2 py-1"
                  onClick={(e) => e.preventDefault()}
                >
                  {neventEncode(block.pointer).slice(0, 12)}…
                </a>
              )
            } else {
              nodes.push(
                <EmbedDepthContext.Provider key={nodes.length} value={depth + 1}>
                  <EmbeddedNote
                    noteId={neventEncode(block.pointer)}
                    className="mt-2"
                    simple={simpleEmbedId === block.pointer.id}
                  />
                </EmbedDepthContext.Provider>
              )
            }
          } else if ('identifier' in block.pointer) {
            if (depth >= maxEmbedDepth) {
              nodes.push(
                <a
                  key={nodes.length}
                  href={toNote(block.pointer)}
                  className="mt-2 inline-block text-sm text-muted-foreground border rounded-lg px-2 py-1"
                  onClick={(e) => e.preventDefault()}
                >
                  {naddrEncode(block.pointer).slice(0, 12)}…
                </a>
              )
            } else {
              nodes.push(
                <EmbedDepthContext.Provider key={nodes.length} value={depth + 1}>
                  <EmbeddedNote noteId={naddrEncode(block.pointer)} className="mt-2" />
                </EmbedDepthContext.Provider>
              )
            }
          } else {
            nodes.push(<EmbeddedMention key={nodes.length} user={nprofileEncode(block.pointer)} />)
          }
          nodes.push(<span key={nodes.length}> </span>)
          break
        }
        case 'hashtag': {
          prevIsNewline = false
          prevIsCandidate = false
          nodes.push(<EmbeddedHashtag key={nodes.length} hashtag={block.value} />)
          nodes.push(<span key={nodes.length}> </span>)
          break
        }
        case 'emoji': {
          prevIsNewline = false
          prevIsCandidate = false
          nodes.push(<Emoji key={nodes.length} classNames={{ img: 'mb-1' }} emoji={block} />)
          break
        }
      }
    }

    if (prevIsCandidate) setPreviouysNodeAsPreview(prevIsCandidate)

    return nodes

    function setPreviouysNodeAsPreview(url: string) {
      const yt = parseYouTubeURL(url)
      if (yt) {
        if (clamp || yt.type === 'playlist' /* the normal renderer can't deal with playlists */) {
          nodes[nodes.length - 1] = (
            <YoutubeStaticEmbed key={nodes.length - 1} id={yt.id} type={yt.type} />
          )
        } else {
          nodes[nodes.length - 1] = (
            <YoutubeEmbeddedPlayer
              key={nodes.length - 1}
              url={url}
              className="mt-2"
              mustLoad={mustLoadMedia}
            />
          )
        }
      } else {
        nodes[nodes.length - 1] = <WebPreview key={nodes.length - 1} className="mt-2" url={url} />
      }
    }
  }, [displayEvent ?? content, simpleEmbedId])

  const contentRef = useRef<HTMLDivElement | null>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    function check() {
      setIsOverflowing(el!.scrollHeight > 300)
    }

    check()

    const observer = new ResizeObserver(check)
    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  const inner = isInflight ? (
    <div className={cn('text-sm text-muted-foreground animate-pulse', className)}>
      {t('Translating…')}
    </div>
  ) : (
    <div
      className={cn('text-wrap break-words whitespace-pre-wrap', className)}
      ref={clamp ? contentRef : undefined}
    >
      {nodes}
    </div>
  )

  if (clamp) {
    return (
      <div className="relative overflow-hidden max-h-[300px]">
        {inner}
        <div
          className={`absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-b from-transparent to-background group-hover/hoverablenote:to-muted pointer-events-none ${
            isOverflowing ? 'opacity-100' : 'opacity-0'
          }`}
        >
          &nbsp;
        </div>
      </div>
    )
  }

  return inner
}
